import { useState, useEffect, useRef } from "react";
import { Wizard, FormField, Input, Select, SpaceBetween, Container, Header, Alert } from "@cloudscape-design/components";
import { getAuthToken } from "./auth";
import { apiFetch } from "./apiFetch";
import AttachStacksForm, { AttachStacksFormHandle } from "./AttachStacksForm";

interface EcsAttachProjectModalProps {
  tagValue: string;
  accountAlias: string;
  onDismiss: () => void;
  onSuccess: (result: { imported: number; updated: number; errors: number }) => void;
  onError: (message: string) => void;
}

export default function EcsAttachProjectModal({ tagValue, accountAlias, onDismiss, onSuccess, onError }: EcsAttachProjectModalProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [projects, setProjects] = useState<{ documentId: string; name: string }[]>([]);
  const [selectedProject, setSelectedProject] = useState<{ value: string; label: string } | null>(null);
  const [createNew, setCreateNew] = useState(false);
  const [newName, setNewName] = useState(tagValue);
  const [newAccount, setNewAccount] = useState<{ value: string; label: string } | null>(null);
  const [accountOptions, setAccountOptions] = useState<{ value: string; label: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(null);
  const formRef = useRef<AttachStacksFormHandle>(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    // Fetch projects and accounts in parallel
    Promise.all([
      apiFetch(`/projects?fields[0]=name&pagination[pageSize]=200`).then((r) => r.json()),
      apiFetch(`/accounts`).then((r) => r.json()),
    ]).then(([projJson, accJson]) => {
      const items = projJson.data ?? [];
      setProjects(items);
      const match = items.find((p: any) => p.name === tagValue);
      if (match) setSelectedProject({ value: match.documentId, label: match.name });

      const opts = (accJson.data ?? []).map((a: any) => ({ value: a.documentId, label: `${a.Alias} (${a.accountid})` }));
      setAccountOptions(opts);
      if (accountAlias) {
        const accMatch = (accJson.data ?? []).find((a: any) => a.Alias === accountAlias);
        if (accMatch) setNewAccount({ value: accMatch.documentId, label: `${accMatch.Alias} (${accMatch.accountid})` });
      }
    }).catch(() => {});
  }, [tagValue, accountAlias]);

  const handleNavigate = async ({ detail }: any) => {
    if (detail.requestedStepIndex === 1 && activeStep === 0) {
      setError("");
      if (createNew) {
        if (!newName.trim()) { setError("Project name is required"); return; }
        setSubmitting(true);
        try {
          const res = await apiFetch(`/projects`, {
            method: "POST",
            body: JSON.stringify({ data: { name: newName.trim(), account: newAccount?.value || null } }),
          });
          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error?.message || `Failed to create project: ${res.status}`);
          }
          const json = await res.json();
          setResolvedProjectId(json.data.documentId);
        } catch (e: any) { setError(e.message); return; } finally { setSubmitting(false); }
      } else {
        if (!selectedProject) { setError("Please select a project"); return; }
        setResolvedProjectId(selectedProject.value);
      }
    }
    setActiveStep(detail.requestedStepIndex);
  };

  return (
    <Wizard
      i18nStrings={{ cancelButton: "Cancel", previousButton: "Previous", nextButton: "Next", submitButton: "Attach Stacks" }}
      activeStepIndex={activeStep}
      onNavigate={handleNavigate}
      onCancel={onDismiss}
      onSubmit={() => formRef.current?.submit()}
      isLoadingNextStep={submitting}
      submitButtonText="Attach Stacks"
      steps={[
        {
          title: "Select Project",
          content: (
            <Container header={<Header variant="h3">Choose or create a project</Header>}>
              <SpaceBetween size="m">
                {error && <Alert type="error">{error}</Alert>}
                <FormField label="Mode">
                  <Select
                    selectedOption={createNew ? { value: "new", label: "Create new project" } : { value: "existing", label: "Use existing project" }}
                    onChange={({ detail }) => setCreateNew(detail.selectedOption.value === "new")}
                    options={[
                      { value: "existing", label: "Use existing project" },
                      { value: "new", label: "Create new project" },
                    ]}
                  />
                </FormField>
                {!createNew ? (
                  <FormField label="Project" description="Select an existing project to attach stacks to">
                    <Select
                      selectedOption={selectedProject}
                      onChange={({ detail }) => setSelectedProject(detail.selectedOption as any)}
                      options={projects.map((p) => ({ value: p.documentId, label: p.name }))}
                      placeholder="Select project"
                      filteringType="auto"
                    />
                  </FormField>
                ) : (
                  <SpaceBetween size="m">
                    <FormField label="Name">
                      <Input value={newName} onChange={({ detail }) => setNewName(detail.value)} />
                    </FormField>
                    <FormField label="Account">
                      <Select
                        selectedOption={newAccount}
                        onChange={({ detail }) => setNewAccount(detail.selectedOption.value === "" ? null : detail.selectedOption as any)}
                        options={[{ value: "", label: "None" }, ...accountOptions]}
                        placeholder="Select account (optional)"
                        filteringType="auto"
                      />
                    </FormField>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </Container>
          ),
        },
        {
          title: "Attach Stacks",
          content: resolvedProjectId ? (
            <AttachStacksForm
              ref={formRef}
              projectId={resolvedProjectId}
              defaultTagValue={tagValue}
              defaultAccountAlias={accountAlias}
              hideSubmitButton
              onSuccess={onSuccess}
              onError={onError}
              onLoadingChange={(loading) => setSubmitting(loading)}
            />
          ) : (
            <Alert type="info">Complete step 1 first.</Alert>
          ),
        },
      ]}
    />
  );
}
